/**
 * Copiado de la app actual (src/pages/checkin/.../MiDniQrCapture/midniQrPayload.ts) sin cambios de lógica.
 * Solo se adapta a TypeScript estricto de v2 (lectura de bytes con `at`, opcionales explícitos).
 */
import { getQrCropBounds } from './midniQrImage'
import type { Point } from './midniQrImage'

const TAG_DOCUMENT_NUMBER = 0x40
const TAG_BIRTH_DATE = 0x42
const TAG_NAME = 0x44
const TAG_SURNAME = 0x46
const TAG_GENDER = 0x48
const TAG_EXPIRATION_DATE = 0x4c
const TAG_ADDRESS = 0x60
const TAG_BIRTH_PLACE_1 = 0x62
const TAG_SUPPORT_NUMBER = 0x68
const TAG_LEGAL_AGE = 0x70
const TAG_ADDRESS_1 = 0x72
const TAG_ADDRESS_2 = 0x74
const TAG_ADDRESS_3 = 0x76
const TAG_BIRTH_PLACE_2 = 0x78
const TAG_BIRTH_PLACE_3 = 0x7a
const TAG_BIDI_EXPIRATION = 0x80
const TAG_SIGNATURE = 0xff

export interface MiDniQrLocalData {
  issuingCountry: string
  signerIdentifier: string
  certificateReference: string
  documentFeature: number
  documentType: number
  documentNumber?: string | undefined
  bornDate?: string | undefined
  name?: string | undefined
  surname?: string | undefined
  gender?: string | undefined
  expirationDate?: string | undefined
  address?: string | undefined
  addressLine1?: string | undefined
  addressLine2?: string | undefined
  addressLine3?: string | undefined
  birthPlace?: string | undefined
  supportNumber?: string | undefined
  legalAge?: string | undefined
  qrExpiration?: string | undefined
}

interface Header {
  issuingCountry: string
  signerIdentifier: string
  certificateReference: string
  headerLength: number
  documentFeature: number
  documentType: number
}

const textDecoder = new TextDecoder('utf-8')

/** Byte en `index`; los llamadores ya comprueban los límites. */
const at = (bytes: Uint8Array, index: number): number => bytes[index] ?? 0

const decodeC40 = (bytes: Uint8Array, offset: number, length: number) => {
  if (offset < 0 || length < 0 || length % 2 !== 0 || offset + length > bytes.length) {
    throw new Error('Invalid MiDNI C40 header')
  }

  let text = ''
  for (let index = offset; index < offset + length; index += 2) {
    const first = at(bytes, index)
    const second = at(bytes, index + 1)
    if (first === 0xfe) {
      text += String.fromCharCode(second - 1)
      continue
    }

    const value = first * 256 + second - 1
    const values = [Math.floor(value / 1600), Math.floor((value % 1600) / 40), value % 40]
    values.forEach((c40Value) => {
      if (c40Value === 3) text += ' '
      else if (c40Value >= 4 && c40Value <= 13) text += String.fromCharCode(48 + c40Value - 4)
      else if (c40Value >= 14 && c40Value <= 39) text += String.fromCharCode(65 + c40Value - 14)
    })
  }
  return text
}

const parseHeader = (bytes: Uint8Array): Header => {
  const issuingCountry = decodeC40(bytes, 2, 2).trim()
  const signerAndLength = decodeC40(bytes, 4, 4).trim()
  if (signerAndLength.length < 6) throw new Error('Invalid MiDNI signer header')

  const certificateReferenceLength = Number.parseInt(signerAndLength.slice(4, 6), 16)
  if (!Number.isFinite(certificateReferenceLength) || certificateReferenceLength < 1) {
    throw new Error('Invalid MiDNI certificate reference')
  }
  const certificateReferenceEncodedLength = Math.ceil(certificateReferenceLength / 3) * 2
  const headerLength = 4 + 4 + certificateReferenceEncodedLength + 3 + 3 + 1 + 1
  if (headerLength > bytes.length) throw new Error('Incomplete MiDNI header')

  const certificateReference = decodeC40(bytes, 8, certificateReferenceEncodedLength)
    .trim()
    .slice(0, certificateReferenceLength)

  return {
    issuingCountry,
    signerIdentifier: signerAndLength.slice(0, 4),
    certificateReference,
    headerLength,
    documentFeature: at(bytes, headerLength - 2),
    documentType: at(bytes, headerLength - 1),
  }
}

const readDerLength = (bytes: Uint8Array, offset: number) => {
  if (offset >= bytes.length) throw new Error('Missing MiDNI field length')
  const first = at(bytes, offset)
  if (first < 0x80) return { length: first, nextOffset: offset + 1 }

  const numberOfBytes = first & 0x7f
  if (numberOfBytes === 0 || numberOfBytes > 4 || offset + numberOfBytes >= bytes.length) {
    throw new Error('Invalid MiDNI field length')
  }
  let length = 0
  for (let index = 0; index < numberOfBytes; index += 1) {
    length = length * 256 + at(bytes, offset + 1 + index)
  }
  return { length, nextOffset: offset + 1 + numberOfBytes }
}

const readText = (fields: Map<number, Uint8Array>, tag: number) => {
  const value = fields.get(tag)
  return value ? textDecoder.decode(value).trim() || undefined : undefined
}

/**
 * Parses a MiDNI VDS-NC payload but deliberately does not verify its signature.
 * It is for development diagnostics only; server-side verification remains authoritative.
 */
export const parseMiDniQrPayload = (bytes: Uint8Array): MiDniQrLocalData => {
  if (bytes.length < 38 || bytes[0] !== 0xdc || bytes[1] !== 0x03) {
    throw new Error('Not a MiDNI VDS-NC v3 payload')
  }

  const header = parseHeader(bytes)
  if (header.issuingCountry !== 'ES' || header.signerIdentifier !== 'ESPN' || header.documentType !== 9) {
    throw new Error('Not a Spanish MiDNI payload')
  }

  const fields = new Map<number, Uint8Array>()
  let offset = header.headerLength
  let hasSignature = false
  while (offset < bytes.length) {
    const tag = at(bytes, offset)
    offset += 1
    const { length, nextOffset } = readDerLength(bytes, offset)
    offset = nextOffset
    if (offset + length > bytes.length) throw new Error('Invalid MiDNI field value length')
    if (tag === TAG_SIGNATURE) {
      hasSignature = length > 0
      break
    }
    fields.set(tag, bytes.slice(offset, offset + length))
    offset += length
  }
  if (!hasSignature) throw new Error('MiDNI payload has no signature')

  return {
    issuingCountry: header.issuingCountry,
    signerIdentifier: header.signerIdentifier,
    certificateReference: header.certificateReference,
    documentFeature: header.documentFeature,
    documentType: header.documentType,
    documentNumber: readText(fields, TAG_DOCUMENT_NUMBER),
    bornDate: readText(fields, TAG_BIRTH_DATE),
    name: readText(fields, TAG_NAME),
    surname: readText(fields, TAG_SURNAME),
    gender: readText(fields, TAG_GENDER),
    expirationDate: readText(fields, TAG_EXPIRATION_DATE),
    address: readText(fields, TAG_ADDRESS),
    addressLine1: readText(fields, TAG_ADDRESS_1),
    addressLine2: readText(fields, TAG_ADDRESS_2),
    addressLine3: readText(fields, TAG_ADDRESS_3),
    birthPlace: readText(fields, TAG_BIRTH_PLACE_1) ?? readText(fields, TAG_BIRTH_PLACE_2) ?? readText(fields, TAG_BIRTH_PLACE_3),
    supportNumber: readText(fields, TAG_SUPPORT_NUMBER),
    legalAge: readText(fields, TAG_LEGAL_AGE),
    qrExpiration: readText(fields, TAG_BIDI_EXPIRATION),
  }
}

export const decodeMiDniQrFromVideoFrame = async (
  video: HTMLVideoElement,
  cornerPoints: Point[]
): Promise<MiDniQrLocalData | undefined> => {
  const bounds = getQrCropBounds(cornerPoints, video.videoWidth, video.videoHeight, 1)
  const canvas = document.createElement('canvas')
  try {
    canvas.width = bounds.width
    canvas.height = bounds.height
    const context = canvas.getContext('2d', { willReadFrequently: true })
    if (!context) throw new Error('Canvas is not available')
    context.drawImage(video, bounds.x, bounds.y, bounds.width, bounds.height, 0, 0, bounds.width, bounds.height)

    const { default: jsQR } = await import('jsqr')
    const imageData = context.getImageData(0, 0, bounds.width, bounds.height)
    const decoded = jsQR(imageData.data, bounds.width, bounds.height, { inversionAttempts: 'dontInvert' })
    return decoded ? parseMiDniQrPayload(Uint8Array.from(decoded.binaryData)) : undefined
  } finally {
    canvas.width = 0
    canvas.height = 0
  }
}
